import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { fetchVariables } from '../api';
import { gpsTable } from '../utils/gpsScore';
import { getMindsetSum } from '../utils/helper';

const GroupReportContext = createContext();

// Switch this to false to work only with the previously cached preview data
// and variables in `groupMembersHashMap` (no /variables API request).
const USE_API_VARIABLES = true;

const getOfflinePreviewData = () => {
  try {
    const storedData = localStorage.getItem('groupReportPreviewData');
    if (!storedData || storedData === 'undefined' || storedData === 'null') return [];

    const parsedData = JSON.parse(storedData);
    const previewData = parsedData?.data || parsedData;
    return Array.isArray(previewData) ? previewData : previewData ? [previewData] : [];
  } catch (error) {
    console.error('Unable to read offline group report variables:', error);
    return [];
  }
};

const sanitizeData = (data) => {
  const seen = new WeakSet();
  return JSON.stringify(data, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }
      seen.add(value);
    }
    return value;
  });
};

export const GroupReportProvider = ({ children }) => {
  const [averagePercentages, setAveragePercentages] = useState()
  const [averagePercentagesLeadership, setAveragePercentagesLeadership] = useState()
  const [averagePercentagesSelf, setAveragePercentagesSelf] = useState()
  const [averagePercentagesLeadershipSelf, setAveragePercentagesLeadershipSelf] = useState()
  const [averagePercentagesMindset, setAveragePercentagesMindset] = useState()
  const [averagePercentagesMindsetSelf, setAveragePercentagesMindsetSelf] = useState()
  const [averagePercentagesLabel, setAveragePercentagesLabel] = useState()
  const [currentTeamData, setCurrentTeamData] = useState()
  const [previewData, setPreviewData] = useState()
  const [reportScores, setReportScores] = useState()
  const [apiData, setApiData] = useState()
  const [openReport, setOpenReport] = useState(true);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [groupReports, setGroupReports] = useState(() => {
    try {
      const savedReports = localStorage.getItem('groupReports');
      return savedReports ? JSON.parse(savedReports) : [];
    } catch (error) {
      console.error('Error loading group reports:', error);
      return [];
    }
  });
  const [selectedReport, setSelectedReport] = useState("")
  const [selectedReportData, setSelectedReportData] = useState([])
  const [selectedAsmtList, setSelectedAsmtList] = useState(JSON.parse(localStorage.getItem("current_selectedAsmtList")) || [])
  const [groupMembersHashMap, setGroupMembersHashMap] = useState()
  const [starterFlow, setStarterFlow] = useState([])

  const addGroupReport = (groupName, assessments, teamName) => {

    const reportId = Date.now();
    const newReport = {
      id: reportId,
      assessment_hash: reportId,
      name: groupName,
      by: "Group report",
      reportType: "Group",
      teamName: teamName,
      remark: ["Group Members", assessments.map(a => a.userName).join(", ")],
      assessments: assessments.map(assessment => assessment.assessment_hash),
      status: "complete"
    };

    setGroupReports(prevReports => {
      const updatedReports = [newReport, ...prevReports];
      try {
        localStorage.setItem('groupReports', sanitizeData(updatedReports));
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
      return updatedReports;
    });

    // Return the assessment_hash so it can be used for preview API call
    return reportId;
  };

  const getAveragePercentageCalculation = useCallback(() => {
    if (!previewData || !selectedReport?.teamId) return;

    // 360 report calculations are now authored by the preview API. Do not
    // recalculate and overwrite the values hydrated from `reportScores`.
    if (selectedReport?.reportType === "360" && reportScores) return;

    let allResults = [];
    let allResultsLeadership = [];
    let allResultsSelf = [];
    let allResultsLeadershipSelf = [];
    const currentTeamData = previewData.find(team => team.teamId === selectedReport.teamId);
    setCurrentTeamData(currentTeamData)
    let resultsArr = currentTeamData?.results;
    if (!resultsArr?.length) return;
    // A team list without member variables (e.g. before /variables loads) can't be
    // scored; skip it instead of overwriting the hydrated scores with zeros.
    if (!resultsArr.some((result) => result?.variables?.length)) return;

    let mindsetPercentSum = 0;
    let mindsetSumSelf = 0;
    let mindsetCount = 0;

    // for starter 
    resultsArr?.forEach(assessment => {
      if (!assessment?.variables) return;
      const variables = {};
      assessment?.variables?.forEach(variable => {
        variables[variable.variable] = Number(variable.score) || 0;
      });
      // --- Use StarterQuardrant logic for this person ---
      const heart_r1_m = variables["heart_round1_m"] || 0;
      const soul_r1_m = variables["soul_round1_m"] || 0;
      const mind_r1_m = variables["mind_round1_m"] || 0;
      const strength_r1_m = variables["str_round1_m"] || 0;
      const heart_r2_m = variables["heart_round2_m"] || 0;
      const soul_r2_m = variables["soul_round2_m"] || 0;
      const mind_r2_m = variables["mind_round2_m"] || 0;
      const strength_r2_m = variables["str_round2_m"] || 0;
      const heart_r1_l = variables["heart_round1_l"] || 0;
      const soul_r1_l = variables["soul_round1_l"] || 0;
      const mind_r1_l = variables["mind_round1_l"] || 0;
      const strength_r1_l = variables["str_round1_l"] || 0;
      const heart_r2_l = variables["heart_round2_l"] || 0;
      const soul_r2_l = variables["soul_round2_l"] || 0;
      const mind_r2_l = variables["mind_round2_l"] || 0;
      const strength_r2_l = variables["str_round2_l"] || 0;

      let most_score = Math.max(heart_r1_m, soul_r1_m, mind_r1_m, strength_r1_m)
      let second_most_score = Math.max(heart_r2_m, soul_r2_m, mind_r2_m, strength_r2_m) / 2
      let least_score = Math.max(heart_r1_l, soul_r1_l, mind_r1_l, strength_r1_l)
      let second_least_score = Math.max(heart_r2_l, soul_r2_l, mind_r2_l, strength_r2_l) / 2

      if (least_score >= second_least_score) {
        least_score = second_least_score - 1;
      }
      const total = most_score + second_most_score + least_score + second_least_score;

      const most_per = (most_score / total) * 100;
      const second_most_per = (second_most_score / total) * 100;
      const least_per = (least_score / total) * 100;
      const second_least_per = (second_least_score / total) * 100;

      const labels_arr = ["heart", "soul", "mind", "strength"];
      const mostValues = [heart_r1_m, soul_r1_m, mind_r1_m, strength_r1_m];
      const secondMostValues = [heart_r2_m, soul_r2_m, mind_r2_m, strength_r2_m];
      const leastValues = [heart_r1_l, soul_r1_l, mind_r1_l, strength_r1_l];
      const secondLeastValues = [heart_r2_l, soul_r2_l, mind_r2_l, strength_r2_l];
      const most_score_label = Math.max(...mostValues);
      const second_most_score_label = Math.max(...secondMostValues);
      const least_score_label = Math.max(...leastValues);
      const second_least_score_label = Math.max(...secondLeastValues);
      const getLabel = (values, maxValue, type) => {
        const hasDuplicates = values.filter(v => v === maxValue).length > 1;

        if (hasDuplicates && type === "2nd_least") {
          const firstIndex = values.indexOf(maxValue);
          const secondIndex = values.findIndex((el, index) => el === maxValue && index !== firstIndex);

          return labels_arr[secondIndex];
        }

        return labels_arr[values.indexOf(maxValue)]; // Default case
      };
      const most_label = getLabel(mostValues, most_score_label);
      const second_most_label = getLabel(secondMostValues, second_most_score_label);
      const least_label = getLabel(leastValues, least_score_label);
      const second_least_label = getLabel(secondLeastValues, second_least_score_label, "2nd_least");

      const getPercentageOrder = (label) => {
        if (label === most_label?.toLowerCase()) return most_per;
        if (label === second_most_label?.toLowerCase()) return second_most_per;
        if (label === least_label?.toLowerCase()) return least_per;
        if (label === second_least_label?.toLowerCase()) return second_least_per;
        return 0;
      };

      // Order: [strength, heart, soul, mind]
      allResults.push([
        getPercentageOrder("strength"),
        getPercentageOrder("heart"),
        getPercentageOrder("soul"),
        getPercentageOrder("mind"),
        [`${assessment.fullName}`]
      ]);

      let sum = [0, 0, 0, 0];
      allResults.forEach(result => {
        sum[0] += result[0]; // strength
        sum[1] += result[1]; // heart
        sum[2] += result[2]; // soul
        sum[3] += result[3]; // mind
      });

      let avg = sum.map(val => val / allResults.length);
      const newStarterAverages = [
        { "avg": Number(avg[0].toFixed(2)), name: "Strength" },
        { "avg": Number(avg[1].toFixed(2)), name: "Heart" },
        { "avg": Number(avg[2].toFixed(2)), name: "Soul" },
        { "avg": Number(avg[3].toFixed(2)), name: "Mind" },
      ];

      setAveragePercentages(prev => {
        if (!prev) return newStarterAverages;
        if (prev.length !== newStarterAverages.length) return newStarterAverages;
        for (let i = 0; i < prev.length; i++) {
          if (prev[i].avg !== newStarterAverages[i].avg || prev[i].name !== newStarterAverages[i].name) {
            return newStarterAverages;
          }
        }
        return prev;
      });

    });

    // for self starter calculation only bro
    resultsArr?.forEach(assessment => {
      if ((assessment?.designation === "360 Assessment" || assessment?.designation === "Self-Assessment") && assessment?.variables?.length) {
        const variables = {};
        assessment?.variables?.forEach(variable => {
          variables[variable.variable] = Number(variable.score) || 0;
        });
        // --- Use StarterQuardrant logic for this person ---
        const heart_r1_m = variables["heart_round1_m"] || 0;
        const soul_r1_m = variables["soul_round1_m"] || 0;
        const mind_r1_m = variables["mind_round1_m"] || 0;
        const strength_r1_m = variables["str_round1_m"] || 0;
        const heart_r2_m = variables["heart_round2_m"] || 0;
        const soul_r2_m = variables["soul_round2_m"] || 0;
        const mind_r2_m = variables["mind_round2_m"] || 0;
        const strength_r2_m = variables["str_round2_m"] || 0;
        const heart_r1_l = variables["heart_round1_l"] || 0;
        const soul_r1_l = variables["soul_round1_l"] || 0;
        const mind_r1_l = variables["mind_round1_l"] || 0;
        const strength_r1_l = variables["str_round1_l"] || 0;
        const heart_r2_l = variables["heart_round2_l"] || 0;
        const soul_r2_l = variables["soul_round2_l"] || 0;
        const mind_r2_l = variables["mind_round2_l"] || 0;
        const strength_r2_l = variables["str_round2_l"] || 0;

        let most_score = Math.max(heart_r1_m, soul_r1_m, mind_r1_m, strength_r1_m)
        let second_most_score = Math.max(heart_r2_m, soul_r2_m, mind_r2_m, strength_r2_m) / 2
        let least_score = Math.max(heart_r1_l, soul_r1_l, mind_r1_l, strength_r1_l)
        let second_least_score = Math.max(heart_r2_l, soul_r2_l, mind_r2_l, strength_r2_l) / 2

        if (least_score >= second_least_score) {
          least_score = second_least_score - 1;
        }
        const total = most_score + second_most_score + least_score + second_least_score;

        const most_per = (most_score / total) * 100;
        const second_most_per = (second_most_score / total) * 100;
        const least_per = (least_score / total) * 100;
        const second_least_per = (second_least_score / total) * 100;

        const labels_arr = ["heart", "soul", "mind", "strength"];
        const mostValues = [heart_r1_m, soul_r1_m, mind_r1_m, strength_r1_m];
        const secondMostValues = [heart_r2_m, soul_r2_m, mind_r2_m, strength_r2_m];
        const leastValues = [heart_r1_l, soul_r1_l, mind_r1_l, strength_r1_l];
        const secondLeastValues = [heart_r2_l, soul_r2_l, mind_r2_l, strength_r2_l];
        const most_score_label = Math.max(...mostValues);
        const second_most_score_label = Math.max(...secondMostValues);
        const least_score_label = Math.max(...leastValues);
        const second_least_score_label = Math.max(...secondLeastValues);
        const getLabel = (values, maxValue, type) => {
          const hasDuplicates = values.filter(v => v === maxValue).length > 1;

          if (hasDuplicates && type === "2nd_least") {
            const firstIndex = values.indexOf(maxValue);
            const secondIndex = values.findIndex((el, index) => el === maxValue && index !== firstIndex);

            return labels_arr[secondIndex];
          }

          return labels_arr[values.indexOf(maxValue)]; // Default case
        };
        const most_label = getLabel(mostValues, most_score_label);
        const second_most_label = getLabel(secondMostValues, second_most_score_label);
        const least_label = getLabel(leastValues, least_score_label);
        const second_least_label = getLabel(secondLeastValues, second_least_score_label, "2nd_least");

        const getPercentageOrder = (label) => {
          if (label === most_label?.toLowerCase()) return most_per;
          if (label === second_most_label?.toLowerCase()) return second_most_per;
          if (label === least_label?.toLowerCase()) return least_per;
          if (label === second_least_label?.toLowerCase()) return second_least_per;
          return 0;
        };

        // Order: [strength, heart, soul, mind]
        allResultsSelf.push([
          getPercentageOrder("strength"),
          getPercentageOrder("heart"),
          getPercentageOrder("soul"),
          getPercentageOrder("mind")
        ]);

        let sum = [0, 0, 0, 0];
        allResultsSelf.forEach(result => {
          sum[0] += result[0]; // strength
          sum[1] += result[1]; // heart
          sum[2] += result[2]; // soul
          sum[3] += result[3]; // mind
        });

        let avg = sum.map(val => val / allResultsSelf.length);
        const newStarterAverages = [
          { "avg": Number(avg[0].toFixed(2)), name: "Strength" },
          { "avg": Number(avg[1].toFixed(2)), name: "Heart" },
          { "avg": Number(avg[2].toFixed(2)), name: "Soul" },
          { "avg": Number(avg[3].toFixed(2)), name: "Mind" },
        ];

        setAveragePercentagesSelf(prev => {
          if (!prev) return newStarterAverages;
          if (prev.length !== newStarterAverages.length) return newStarterAverages;
          for (let i = 0; i < prev.length; i++) {
            if (prev[i].avg !== newStarterAverages[i].avg || prev[i].name !== newStarterAverages[i].name) {
              return newStarterAverages;
            }
          }
          return prev;
        });

        // self mindset calculation starts here
        mindsetSumSelf = getMindsetSum(assessment?.variables);
      }
    });

    // for leadership & mindset calculation bro
    resultsArr?.forEach(assessment => {
      if (!assessment?.variables) return;
      const variables = {};
      assessment?.variables?.forEach(variable => {
        variables[variable.variable] = Number(variable.score) || 0;
      });
      // --- Use Leadership logic for this person ---
      const performance_r1_m = variables["per_round1_m"] || 0;
      const strategic_r1_m = variables["strat_round1_m"] || 0;
      const visionary_r1_m = variables["vis_round1_m"] || 0;
      const collaborative_r1_m = variables["col_round1_m"] || 0;
      const performance_r2_m = variables["per_round2_m"] || 0;
      const strategic_r2_m = variables["strat_round2_m"] || 0;
      const visionary_r2_m = variables["vis_round2_m"] || 0;
      const collaborative_r2_m = variables["col_round2_m"] || 0;
      const performance_r1_l = variables["per_round1_l"] || 0;
      const strategic_r1_l = variables["strat_round1_l"] || 0;
      const visionary_r1_l = variables["vis_round1_l"] || 0;
      const collaborative_r1_l = variables["col_round1_l"] || 0;
      const performance_r2_l = variables["per_round2_l"] || 0;
      const strategic_r2_l = variables["strat_round2_l"] || 0;
      const visionary_r2_l = variables["vis_round2_l"] || 0;
      const collaborative_r2_l = variables["col_round2_l"] || 0;

      // Calculate highest scores for each quadrant
      const collaborative_highest = Math.max(collaborative_r1_m, collaborative_r2_m, collaborative_r1_l, collaborative_r2_l);
      const strategic_highest = Math.max(strategic_r1_m, strategic_r2_m, strategic_r1_l, strategic_r2_l);
      const performance_highest = Math.max(performance_r1_m, performance_r2_m, performance_r1_l, performance_r2_l);
      const visionary_highest = Math.max(visionary_r1_m, visionary_r2_m, visionary_r1_l, visionary_r2_l);
      // Calculate most and second most scores
      const most_scores = [performance_r1_m, strategic_r1_m, visionary_r1_m, collaborative_r1_m];
      const second_most_scores = [performance_r2_m, strategic_r2_m, visionary_r2_m, collaborative_r2_m].map(score => score / 2);
      const least_scores = [performance_r1_l, strategic_r1_l, visionary_r1_l, collaborative_r1_l];
      const second_least_scores = [performance_r2_l, strategic_r2_l, visionary_r2_l, collaborative_r2_l].map(score => score / 2);

      const most_score = Math.max(...most_scores);
      const second_most_score = Math.max(...second_most_scores);
      let least_score = Math.max(...least_scores);
      const second_least_score = Math.max(...second_least_scores);
      if (least_score >= second_least_score) {
        least_score = second_least_score - 1;
      }

      const total = most_score + second_most_score + least_score + second_least_score;
      const most_per = (most_score / total) * 100;
      const second_most_per = (second_most_score / total) * 100;
      const least_per = (least_score / total) * 100;
      const second_least_per = (second_least_score / total) * 100;
      const labels_arr = ["performance", "strategic", "visionary", "collaborative"];
      const mostValues = [performance_r1_m, strategic_r1_m, visionary_r1_m, collaborative_r1_m];
      const secondMostValues = [performance_r2_m, strategic_r2_m, visionary_r2_m, collaborative_r2_m];
      const leastValues = [performance_r1_l, strategic_r1_l, visionary_r1_l, collaborative_r1_l];
      const secondLeastValues = [performance_r2_l, strategic_r2_l, visionary_r2_l, collaborative_r2_l];
      const most_score_label = Math.max(...mostValues);
      const second_most_score_label = Math.max(...secondMostValues);
      const least_score_label = Math.max(...leastValues);
      const second_least_score_label = Math.max(...secondLeastValues);
      const getLabel = (values, maxValue, type) => {
        const hasDuplicates = values.filter(v => v === maxValue).length > 1;

        if (hasDuplicates && type === "2nd_least") {
          const firstIndex = values.indexOf(maxValue);
          const secondIndex = values.findIndex((el, index) => el === maxValue && index !== firstIndex);

          return labels_arr[secondIndex];
        }

        return labels_arr[values.indexOf(maxValue)]; // Default case
      };
      const most_label = getLabel(mostValues, most_score_label);
      const second_most_label = getLabel(secondMostValues, second_most_score_label);
      const least_label = getLabel(leastValues, least_score_label);
      const second_least_label = getLabel(secondLeastValues, second_least_score_label, "2nd_least");

      const getPercentageOrder = (label) => {
        if (label === most_label?.toLowerCase()) return most_per;
        if (label === second_most_label?.toLowerCase()) return second_most_per;
        if (label === least_label?.toLowerCase()) return least_per;
        if (label === second_least_label?.toLowerCase()) return second_least_per;
        return 0;
      };

      // Order: [strategic, visionary, collaborative, performance]
      allResultsLeadership.push([
        getPercentageOrder("strategic"),
        getPercentageOrder("visionary"),
        getPercentageOrder("collaborative"),
        getPercentageOrder("performance"),
        [`${assessment.fullName}`]
      ]);

      let sum = [0, 0, 0, 0];
      allResultsLeadership.forEach(result => {
        sum[0] += result[0]; // strategic
        sum[1] += result[1]; // visionary
        sum[2] += result[2]; // collaborative
        sum[3] += result[3]; // performance
      });

      let avg = sum.map(val => val / allResultsLeadership.length);

      const newLeadershipAverages = [
        { "avg": Number(avg[0].toFixed(2)), name: "Strategic" },
        { "avg": Number(avg[1].toFixed(2)), name: "Visionary" },
        { "avg": Number(avg[2].toFixed(2)), name: "Collaborative" },
        { "avg": Number(avg[3].toFixed(2)), name: "Performance" },
      ];


      // console.log("allResultsLeadership",allResultsLeadership)

      setAveragePercentagesLeadership(prev => {
        if (!prev) return newLeadershipAverages;
        if (prev.length !== newLeadershipAverages.length) return newLeadershipAverages;
        for (let i = 0; i < prev.length; i++) {
          if (prev[i].avg !== newLeadershipAverages[i].avg || prev[i].name !== newLeadershipAverages[i].name) {
            return newLeadershipAverages;
          }
        }
        return prev;
      });


      // mindset calculation starts here (normalize each user's mindset first, then average)
      const assessmentMindsetSum = getMindsetSum(assessment?.variables);
      if (assessmentMindsetSum > 0) {
        const assessmentMindsetPercent = getPercentileFromScore(assessmentMindsetSum);
        mindsetPercentSum += Number(assessmentMindsetPercent) || 0;
        mindsetCount++;
      }

    });

    // console.log("newStarterAverages, allResultsLeadership360", allResultsLeadership)

    // for self leadership calculation only bro
    resultsArr?.forEach(assessment => {
      if (assessment?.designation !== "360 Assessment" && assessment?.designation !== "Self-Assessment") return;
      if (!assessment?.variables?.length) return;
      const variables = {};
      assessment?.variables?.forEach(variable => {
        variables[variable.variable] = Number(variable.score) || 0;
      });
      // --- Use Leadership logic for this person ---
      const performance_r1_m = variables["per_round1_m"] || 0;
      const strategic_r1_m = variables["strat_round1_m"] || 0;
      const visionary_r1_m = variables["vis_round1_m"] || 0;
      const collaborative_r1_m = variables["col_round1_m"] || 0;
      const performance_r2_m = variables["per_round2_m"] || 0;
      const strategic_r2_m = variables["strat_round2_m"] || 0;
      const visionary_r2_m = variables["vis_round2_m"] || 0;
      const collaborative_r2_m = variables["col_round2_m"] || 0;
      const performance_r1_l = variables["per_round1_l"] || 0;
      const strategic_r1_l = variables["strat_round1_l"] || 0;
      const visionary_r1_l = variables["vis_round1_l"] || 0;
      const collaborative_r1_l = variables["col_round1_l"] || 0;
      const performance_r2_l = variables["per_round2_l"] || 0;
      const strategic_r2_l = variables["strat_round2_l"] || 0;
      const visionary_r2_l = variables["vis_round2_l"] || 0;
      const collaborative_r2_l = variables["col_round2_l"] || 0;

      // Calculate highest scores for each quadrant
      const collaborative_highest = Math.max(collaborative_r1_m, collaborative_r2_m, collaborative_r1_l, collaborative_r2_l);
      const strategic_highest = Math.max(strategic_r1_m, strategic_r2_m, strategic_r1_l, strategic_r2_l);
      const performance_highest = Math.max(performance_r1_m, performance_r2_m, performance_r1_l, performance_r2_l);
      const visionary_highest = Math.max(visionary_r1_m, visionary_r2_m, visionary_r1_l, visionary_r2_l);
      // Calculate most and second most scores
      const most_scores = [performance_r1_m, strategic_r1_m, visionary_r1_m, collaborative_r1_m];
      const second_most_scores = [performance_r2_m, strategic_r2_m, visionary_r2_m, collaborative_r2_m].map(score => score / 2);
      const least_scores = [performance_r1_l, strategic_r1_l, visionary_r1_l, collaborative_r1_l];
      const second_least_scores = [performance_r2_l, strategic_r2_l, visionary_r2_l, collaborative_r2_l].map(score => score / 2);

      const most_score = Math.max(...most_scores);
      const second_most_score = Math.max(...second_most_scores);
      let least_score = Math.max(...least_scores);
      const second_least_score = Math.max(...second_least_scores);
      if (least_score >= second_least_score) {
        least_score = second_least_score - 1;
      }

      const total = most_score + second_most_score + least_score + second_least_score;
      const most_per = (most_score / total) * 100;
      const second_most_per = (second_most_score / total) * 100;
      const least_per = (least_score / total) * 100;
      const second_least_per = (second_least_score / total) * 100;
      const labels_arr = ["performance", "strategic", "visionary", "collaborative"];
      const mostValues = [performance_r1_m, strategic_r1_m, visionary_r1_m, collaborative_r1_m];
      const secondMostValues = [performance_r2_m, strategic_r2_m, visionary_r2_m, collaborative_r2_m];
      const leastValues = [performance_r1_l, strategic_r1_l, visionary_r1_l, collaborative_r1_l];
      const secondLeastValues = [performance_r2_l, strategic_r2_l, visionary_r2_l, collaborative_r2_l];
      const most_score_label = Math.max(...mostValues);
      const second_most_score_label = Math.max(...secondMostValues);
      const least_score_label = Math.max(...leastValues);
      const second_least_score_label = Math.max(...secondLeastValues);
      const getLabel = (values, maxValue) => labels_arr[values.indexOf(maxValue)];
      const most_label = getLabel(mostValues, most_score_label);
      const second_most_label = getLabel(secondMostValues, second_most_score_label);
      const least_label = getLabel(leastValues, least_score_label);
      const second_least_label = getLabel(secondLeastValues, second_least_score_label, "2nd_least");

      const getPercentageOrder = (label) => {
        if (label === most_label?.toLowerCase()) return most_per;
        if (label === second_most_label?.toLowerCase()) return second_most_per;
        if (label === least_label?.toLowerCase()) return least_per;
        if (label === second_least_label?.toLowerCase()) return second_least_per;
        return 0;
      };

      // Order: [strategic, visionary, collaborative, performance]
      allResultsLeadershipSelf.push([
        getPercentageOrder("strategic"),
        getPercentageOrder("visionary"),
        getPercentageOrder("collaborative"),
        getPercentageOrder("performance")
      ]);

      let sum = [0, 0, 0, 0];
      allResultsLeadershipSelf.forEach(result => {
        sum[0] += result[0]; // strategic
        sum[1] += result[1]; // visionary
        sum[2] += result[2]; // collaborative
        sum[3] += result[3]; // performance
      });

      let avg = sum.map(val => val / allResultsLeadershipSelf.length);
      const newSelfAverages = [
        { "avg": Number(avg[0].toFixed(2)), name: "Strategic" },
        { "avg": Number(avg[1].toFixed(2)), name: "Visionary" },
        { "avg": Number(avg[2].toFixed(2)), name: "Collaborative" },
        { "avg": Number(avg[3].toFixed(2)), name: "Performance" },
      ];

      // console.log("assessment",assessment)
      setAveragePercentagesLeadershipSelf(prev => {
        if (!prev) return newSelfAverages;
        if (prev.length !== newSelfAverages.length) return newSelfAverages;
        for (let i = 0; i < prev.length; i++) {
          if (prev[i].avg !== newSelfAverages[i].avg || prev[i].name !== newSelfAverages[i].name) {
            return newSelfAverages;
          }
        }
        return prev;
      });
    });

    // Calculate group mindset percentage by averaging per-user normalized scores
    const mindsetPercent = mindsetCount > 0 ? (mindsetPercentSum / mindsetCount) : 0;
    setAveragePercentagesMindset(prev => {
      const newValue = Number((mindsetPercent || 0).toFixed(1));
      return prev === newValue ? prev : newValue;
    });

    // Calculate self mindset percentage
    // const mindsetPercentSelf = mindsetSumSelf / (84) * 100;
    const mindsetPercentSelf = getPercentileFromScore(mindsetSumSelf)
    setAveragePercentagesMindsetSelf(mindsetPercentSelf);
    // console.log("mindsetPercentSelf,mindsetPercent)",mindsetPercentSelf,mindsetPercent)

  }, [previewData, selectedReport?.teamId, selectedReport?.reportType, groupMembersHashMap, reportScores]);

  const getAverageScore = useCallback((designation, allResults, index) => {
    const scores = allResults?.[index]?.individualScores
      ?.filter(el => el?.designation === designation)
      ?.map(el => Number(el?.score))
      ?.filter(score => !isNaN(score));

    if (!scores?.length) return "-";

    const peopleWhoScored = scores?.filter((el) => el > 0 )

    if (!peopleWhoScored?.length) return "-";

    // const average = scores.reduce((acc, val) => acc + val, 0) / scores.length; // all people
    const average = scores.reduce((acc, val) => acc + val, 0) / peopleWhoScored.length; // dividing by no of people who've scored something

    return average === 0 ? "-" : average.toFixed(1);
  }, []);

  // Item-overview averages for 360 reports exclude the respondent's Self (S)
  // rating: A is the average of every observer who scored the item, rounded to
  // one decimal, exactly as the report API and the PDF calculate it. The API
  // already supplies it (displayScores[5]), so use that; apply the same rule
  // to the individual scores only when it is missing. Returned formatted (like
  // getAverageScore) so whole numbers keep their ".0" on the In Focus pages.
  const getObserverAverageScore = useCallback((allResults, index) => {
    const apiAverage = allResults?.[index]?.displayScores?.[5];
    if (apiAverage !== undefined && apiAverage !== null && apiAverage !== "") return String(apiAverage);

    const individualScores = allResults?.[index]?.individualScores || [];
    const observerDesignations = [
      "360 Assessment Manager",
      "360 Assessment Direct Report",
      "360 Assessment Peer",
      "360 Assessment Other",
    ];

    const scores = individualScores
      .filter((entry) => observerDesignations.includes(entry?.designation))
      .map((entry) => Number(entry?.score))
      .filter((score) => Number.isFinite(score) && score > 0);

    if (!scores.length) return "-";

    return (scores.reduce((sum, score) => sum + score, 0) / scores.length).toFixed(1);
  }, []);

  const calculateQuestionScore = useCallback((quadrant_score, allVariableSets, question_no, section_type, allAssessmentDesignation, allAssessmentHash, allAssessmentFullName) => {
    // The 360 preview endpoint supplies these scores already calculated by
    // the backend. Keep the former browser calculation below as a fallback
    // for report types or legacy API responses without this data.
    let backendScore;
    if (selectedReport?.reportType === "360") {
      const question = reportScores?.calculateQuestionScore?.find(
        (item) => Number(item?.questionNumber) === Number(question_no)
      );
      backendScore = question?.quadrants?.[String(quadrant_score)];
    }

    const scoreMap = {
      R1M: 4,
      R1L: 1,
      R2M: 3,
      R2L: 2
    };

    const variableno = (section_type === "in_focus" || section_type === "360_overview") ? question_no : (Math.floor(question_no / 4) + 1);

    // console.log("allResults,quadrant_score,variableno,section_type",quadrant_score, variableno, section_type )
    const keyMap = [
      { key: `st${variableno}_self_pass1`, label: 'R1M' },
      { key: `st${variableno}_self_pass2`, label: 'R1L' },
      { key: `st${variableno + 10}_self_pass1`, label: 'R2M' },
      { key: `st${variableno + 10}_self_pass2`, label: 'R2L' }
    ];

    const individualScores = [];
    let questionId = null;
    let index = 0;

    // R2Least > R2Most > R1Least > R1Most : priority logic

    for (const dataSet of allVariableSets) {
      const variables = dataSet || [];
      let temp = 0;

      for (const variable of variables) {
        if (variable.score === quadrant_score) {
          // whoever working on this please remember temp gets overwritten because priority logic is there
          if (variable.variable === keyMap[3]?.key && (temp === 0 || temp === 1 || temp === 3 || temp === 4)) {
            temp = scoreMap?.[keyMap[3].label];
          }
          else if (variable.variable === keyMap[2]?.key && (temp === 0 || temp === 4 || temp === 1)) {
            temp = scoreMap?.[keyMap[2].label];
          }
          else if (variable.variable === keyMap[1]?.key && (temp === 0 || temp === 4)) {
            temp = scoreMap?.[keyMap[1].label];
          }
          else if (variable.variable === keyMap[0]?.key && (temp === 0)) {
            temp = scoreMap?.[keyMap[0].label];
          }
        }
      }

      // console.log("dataSet",dataSet)

      individualScores.push({ "score": temp, designation: allAssessmentDesignation?.[index], "hash": allAssessmentHash?.[index], "name": allAssessmentFullName?.[index] })

      index++;
    }

    let sum = 0;
    individualScores?.forEach(element => {
      sum = sum + element?.score || 0;
    });

    const peopleWhoScored = individualScores?.filter((el) => el.score > 0 )

    // const averageScore = (sum / individualScores?.length).toFixed(1) || 0; // dividing by number of all people
    const averageScore = peopleWhoScored?.length ? (sum / peopleWhoScored.length).toFixed(1) : "-"; // dividing by number of all people who've scored something; "-" when nobody scored
    
    // console.log("individualScores,averageScore",individualScores,averageScore)
    const localResult = {
      questionId,
      individualScores,
      averageScore
    };

    if (!backendScore) return localResult;

    // Keep the API's observer scores (which drive A), but provide the self
    // response from the raw member variables for the visible S column.
    const selfScores = individualScores
      .filter((entry) => ["360 Assessment", "Self-Assessment", "Self"].includes(entry?.designation))
      .map((entry) => ({ ...entry, designation: "360 Assessment" }));

    return {
      ...backendScore,
      individualScores: [
        ...(backendScore.individualScores || []).filter(
          (entry) => !["360 Assessment", "Self-Assessment", "Self"].includes(entry?.designation)
        ),
        ...selfScores,
      ],
    };
  }, [reportScores, selectedReport?.reportType]);

  const calculateQuestionScoreLeadership = useCallback((quadrant_score, allVariableSets, question_no, section_type, allAssessmentDesignation, allAssessmentHash, allAssessmentFullName) => {
    let backendScore;
    if (selectedReport?.reportType === "360") {
      const question = reportScores?.calculateQuestionScoreLeadership?.find(
        (item) => Number(item?.questionNumber) === Number(question_no)
      );
      backendScore = question?.quadrants?.[String(quadrant_score)];
    }

    const scoreMap = {
      R1M: 4,
      R1L: 1,
      R2M: 3,
      R2L: 2
    };

    const variableno = (section_type === "in_focus" || section_type === "360_overview") ? question_no : (Math.floor(question_no / 4) + 1);

    // console.log("apiData, quadrant_score, allVariableSets, variableno",apiData, quadrant_score, allVariableSets, variableno)
    const keyMap = [
      { key: `ls${variableno}_self_pass1`, label: 'R1M' },
      { key: `ls${variableno}_self_pass2`, label: 'R1L' },
      { key: `ls${variableno + 10}_self_pass1`, label: 'R2M' },
      { key: `ls${variableno + 10}_self_pass2`, label: 'R2L' }
    ];

    const individualScores = [];
    let questionId = null;
    let index = 0;

    // R2Least > R2Most > R1Least > R1Most : priority logic

    for (const dataSet of allVariableSets) {
      const variables = dataSet || [];
      let temp = 0;

      for (const variable of variables) {
        if (variable.score === quadrant_score) {
          // whoever working on this please remember temp gets overwritten because priority logic is there
          if (variable.variable === keyMap[3]?.key && (temp === 0 || temp === 1 || temp === 3 || temp === 4)) {
            temp = scoreMap?.[keyMap[3].label];
          }
          else if (variable.variable === keyMap[2]?.key && (temp === 0 || temp === 4 || temp === 1)) {
            temp = scoreMap?.[keyMap[2].label];
          }
          else if (variable.variable === keyMap[1]?.key && (temp === 0 || temp === 4)) {
            temp = scoreMap?.[keyMap[1].label];
          }
          else if (variable.variable === keyMap[0]?.key && (temp === 0)) {
            temp = scoreMap?.[keyMap[0].label];
          }
        }
      }
      individualScores.push({ "score": temp, designation: allAssessmentDesignation?.[index], "hash": allAssessmentHash?.[index], "name": allAssessmentFullName?.[index], variableno, question_no })

      index++;
    }

    let sum = 0;
    individualScores?.forEach(element => {
      sum = sum + element?.score || 0;
    });

    const peopleWhoScored = individualScores?.filter((el) => el.score > 0 )

    // const averageScore = (sum / individualScores?.length).toFixed(1) || 0; // dividing by number of all people
    const averageScore = peopleWhoScored?.length ? (sum / peopleWhoScored.length).toFixed(1) : "-"; // dividing by number of all people who've scored something; "-" when nobody scored

    const localResult = {
      questionId,
      individualScores,
      averageScore
    };

    if (!backendScore) return localResult;

    const selfScores = individualScores
      .filter((entry) => ["360 Assessment", "Self-Assessment", "Self"].includes(entry?.designation))
      .map((entry) => ({ ...entry, designation: "360 Assessment" }));

    return {
      ...backendScore,
      individualScores: [
        ...(backendScore.individualScores || []).filter(
          (entry) => !["360 Assessment", "Self-Assessment", "Self"].includes(entry?.designation)
        ),
        ...selfScores,
      ],
    };
  }, [reportScores, selectedReport?.reportType]);


  useEffect(() => {
    try {
      localStorage.setItem('groupReports', sanitizeData(groupReports));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [groupReports]);

  useEffect(() => {
    const teamId = selectedReport?.teamId || groupMembersHashMap?.teamId;
    if (!teamId) return;

    if (!USE_API_VARIABLES) {
      const offlineVariables = groupMembersHashMap?.hashMap || {};
      const offlinePreviewData = getOfflinePreviewData().map((team) => {
        if (team?.teamId !== teamId) return team;

        return {
          ...team,
          results: team?.results?.map((result) => ({
            ...result,
            variables: offlineVariables[result?.hash] || result?.variables,
          })),
        };
      });

      setPreviewData(offlinePreviewData);
      return;
    }

    let isCurrentRequest = true;

    const setScoreIfPresent = (scores, key, setter) => {
      if (Object.prototype.hasOwnProperty.call(scores, key)) {
        setter(scores[key]);
      }
    };

    (async () => {
      try {
        // This endpoint returns both the member variables and the saved report
        // calculations in `reportScores`.
        const data = await fetchVariables(teamId);
        if (!isCurrentRequest || !data) return;

        const scores = data.reportScores || data?.data?.reportScores;
        const results = data.results || data?.data?.results || [];
        const variablesByHash = results.reduce((hashMap, result) => {
          if (result?.hash) hashMap[result.hash] = result.variables;
          return hashMap;
        }, {});

        if (!scores || typeof scores !== 'object') return;

        // Some saved reports keep the member variables inside reportScores,
        // while newer responses return them at the top level. Support both.
        const scoreHashMap = scores?.groupMembersHashMap?.hashMap;
        const memberVariables = Object.keys(variablesByHash).length
          ? variablesByHash
          : scoreHashMap;

        // Keep member variables current so the existing calculations below can
        // continue to calculate exactly as before.
        if (memberVariables && Object.keys(memberVariables).length) {
          setGroupMembersHashMap({ teamId, hashMap: memberVariables });
        }

        setReportScores(scores);
        setScoreIfPresent(scores, 'averagePercentages', setAveragePercentages);
        setScoreIfPresent(scores, 'averagePercentagesLeadership', setAveragePercentagesLeadership);
        setScoreIfPresent(scores, 'averagePercentagesSelf', setAveragePercentagesSelf);
        setScoreIfPresent(scores, 'averagePercentagesLeadershipSelf', setAveragePercentagesLeadershipSelf);
        setScoreIfPresent(scores, 'averagePercentagesMindset', setAveragePercentagesMindset);
        setScoreIfPresent(scores, 'averagePercentagesMindsetSelf', setAveragePercentagesMindsetSelf);
        setScoreIfPresent(scores, 'averagePercentagesLabel', setAveragePercentagesLabel);
        setScoreIfPresent(scores, 'currentTeamData', setCurrentTeamData);
        if (Array.isArray(scores.previewData)) {
          // Preserve the previous context contract: the selected team's result
          // entries always include their variables.
          const previewWithVariables = scores.previewData.map((team) => {
            if (team?.teamId !== teamId || !memberVariables) return team;

            return {
              ...team,
              results: team?.results?.map((result) => ({
                ...result,
                variables: memberVariables[result?.hash] || result?.variables,
              })),
            };
          });
          setPreviewData(previewWithVariables);
        }
        setScoreIfPresent(scores, 'selectedReportData', setSelectedReportData);
        setScoreIfPresent(scores, 'selectedAsmtList', setSelectedAsmtList);
        setScoreIfPresent(scores, 'starterFlow', setStarterFlow);

        // `openReport` is transient UI state owned by the View Report handler.
        // Restoring its saved value here can immediately close a report after
        // the user opens it, so it is intentionally not hydrated.
        // Do not replace an active selection. The stored selection is useful
        // only when this context is restored before a report has been chosen.
        if (!selectedReport && scores.selectedReport) {
          setSelectedReport(scores.selectedReport);
        }
        if (!selectedReportType && scores.selectedReportType) {
          setSelectedReportType(scores.selectedReportType);
        }
      } catch (error) {
        console.error('Error fetching report variables and scores:', error);
      }
    })();

    return () => {
      isCurrentRequest = false;
    };
  }, [selectedReport?.teamId, groupMembersHashMap?.teamId]);


  useEffect(() => {
    if (previewData?.length > 0 && selectedReport?.teamId) {
      // console.log("previewData, groupMembersHashMap, selectedReport",previewData, groupMembersHashMap, selectedReport)
      getAveragePercentageCalculation();
    }
  }, [previewData, groupMembersHashMap, selectedReport])

  useEffect(() => {
    localStorage.setItem("current_selectedAsmtList", JSON.stringify(selectedAsmtList))
  }, [selectedAsmtList])


const getPercentileFromScore = (totalScore, table = gpsTable) => {
  const score = Math.round(Number(totalScore - 12))
  const min = table[0]
  const max = table[table.length - 1]

  if (score <= min.score) return 0
  if (score >= max.score) return 100

  const exact = table.find((row) => row.score === score)
  if (exact) return exact.percentile

  const nearest = table.reduce((a, b) => {
    const d1 = Math.abs(a.score - score)
    const d2 = Math.abs(b.score - score)
    return d2 < d1 ? b : a
  })
  return nearest.percentile
}



  // console.log("selectedReport,groupMembersHashMap",selectedReport,groupMembersHashMap, previewData)
  // console.log("averagePercentagesSelf,averagePercentagesLeadershipSelf",averagePercentagesSelf,averagePercentagesLeadershipSelf)


  const providerValue = useMemo(() => ({
    groupReports,
    addGroupReport,
    averagePercentages,
    averagePercentagesLeadership,
    averagePercentagesSelf,
    averagePercentagesLeadershipSelf,
    averagePercentagesMindset,
    setPreviewData,
    previewData,
    reportScores,
    setApiData,
    calculateQuestionScore,
    calculateQuestionScoreLeadership,
    getAverageScore,
    getObserverAverageScore,
    setSelectedReport,
    selectedReport,
    setSelectedReportData,
    selectedReportData,
    selectedAsmtList,
    setSelectedAsmtList,
    groupMembersHashMap,
    setGroupMembersHashMap,
    starterFlow,
    setStarterFlow,
    setOpenReport,
    openReport,
    selectedReport,
    setSelectedReport,
    selectedReportType,
    setSelectedReportType,
    currentTeamData,
    averagePercentagesMindsetSelf,
    setAveragePercentagesMindsetSelf,
    getPercentileFromScore
  }), [
    groupReports,
    averagePercentages,
    averagePercentagesLeadership,
    averagePercentagesSelf,
    averagePercentagesLeadershipSelf,
    averagePercentagesMindset,
    calculateQuestionScore,
    calculateQuestionScoreLeadership,
    getAverageScore,
    getObserverAverageScore,
    previewData,
    reportScores,
    selectedReport,
    selectedReportData,
    selectedAsmtList,
    groupMembersHashMap,
    starterFlow,
    setOpenReport,
    openReport,
    selectedReport,
    setSelectedReport,
    selectedReportType,
    setSelectedReportType,
    currentTeamData,
    averagePercentagesMindsetSelf,
    setAveragePercentagesMindsetSelf,
    getPercentileFromScore
  ]);

  return (
    <GroupReportContext.Provider value={providerValue}>
      {children}
    </GroupReportContext.Provider>
  );
};

export const useGroupReport = () => useContext(GroupReportContext);

export default GroupReportContext;
